/**
 * Microcourse Studio — 数据格式转换器
 * 将旧版 SlideDeck / LessonDocument 格式转换为统一的 MicrocourseProject
 */

import type {
  MicrocourseProject,
  MicroSlide,
  MicroElement,
  NarrationSegment,
  QuizElement,
  QuizContent,
  LegacySlideDeck,
  LegacySlideContent,
  LegacyQuizBlock,
  LegacyLessonDocument,
  LegacyLessonBlock,
} from './types.js'
import { DEFAULT_PROJECT_SETTINGS, FORMAT_VERSION_CURRENT } from './constants.js'

// ─── ID Generator ──────────────────────────────────────────────────

let idCounter = 0
function genId(prefix = 'mc'): string {
  idCounter++
  return `${prefix}_${Date.now().toString(36)}_${idCounter.toString(36)}_${Math.random().toString(36).slice(2, 7)}`
}

// ─── Quiz 内容归一化（存量旧结构兼容层） ────────────────────────

/**
 * 将 quiz content 归一化为新结构（与题库 questions 对齐）。
 * 存量旧结构（quizType/question/correctOptionId/...）自动转换；
 * 已是新结构（type/stem/answer.value）则直接返回。
 */
export function normalizeQuizContent(raw: unknown): QuizContent {
  const r = (raw ?? {}) as Record<string, any>

  // 已是新结构
  if (typeof r.type === 'string' && typeof r.stem === 'string') {
    return {
      type: r.type as QuizContent['type'],
      stem: r.stem,
      images: Array.isArray(r.images) ? r.images : undefined,
      options: Array.isArray(r.options) ? r.options : undefined,
      answer: r.answer && typeof r.answer.value === 'string' ? { value: r.answer.value } : { value: '' },
      analysis: typeof r.analysis === 'string' ? r.analysis : undefined,
    }
  }

  // 旧结构转换
  const label = (i: number) => String.fromCharCode(65 + i)
  const legacyOptions = Array.isArray(r.options)
    ? (r.options as Array<{ id?: string; text?: string; imageUrl?: string }>)
    : []
  const images = r.imageUrl ? [r.imageUrl as string] : undefined
  const analysis = typeof r.explanation === 'string' ? r.explanation : undefined

  if (r.quizType === 'single_choice' || r.quizType === 'multiple_choice') {
    const options = legacyOptions.map((o, i) => ({
      label: label(i),
      content: o.text ?? '',
      ...(o.imageUrl ? { images: [o.imageUrl] } : {}),
    }))
    let value = ''
    if (r.quizType === 'single_choice') {
      const idx = legacyOptions.findIndex((o) => o.id === r.correctOptionId)
      value = idx >= 0 ? label(idx) : ''
    } else {
      const ids: string[] = Array.isArray(r.correctOptionIds) ? r.correctOptionIds : []
      value = legacyOptions.map((o, i) => (o.id && ids.includes(o.id) ? label(i) : '')).join('')
    }
    return {
      type: r.quizType === 'single_choice' ? 'choice' : 'multi_choice',
      stem: r.question ?? '',
      images,
      options,
      answer: { value },
      analysis,
    }
  }
  if (r.quizType === 'true_false') {
    return {
      type: 'true_false',
      stem: r.question ?? '',
      images,
      answer: { value: r.correctAnswer ? '对' : '错' },
      analysis,
    }
  }
  // fill_blank：答案在 fillText 的 {{...}} 占位符内
  const m = typeof r.fillText === 'string' ? r.fillText.match(/\{\{(.+?)\}\}/) : null
  return {
    type: 'fill_blank',
    stem: r.question ?? '',
    images,
    answer: { value: m?.[1] ?? '' },
    analysis,
  }
}

// ─── SlideDeck → MicrocourseProject ────────────────────────────────

/**
 * 将旧版 AI 微课工作台的 SlideDeck 格式转换为 MicrocourseProject
 */
export function slideDeckToProject(deck: LegacySlideDeck, lessonId?: string): MicrocourseProject {
  const projectId = lessonId ?? deck.deck_id ?? genId('proj')
  const chapterId = genId('ch')

  const slides: MicroSlide[] = deck.slides.map((slide, index) =>
    convertLegacySlide(slide, index, chapterId)
  )

  return {
    id: projectId,
    title: deck.topic || '未命名微课',
    theme: DEFAULT_PROJECT_SETTINGS.theme,
    customColors: { ...DEFAULT_PROJECT_SETTINGS.customColors },
    fontFamily: DEFAULT_PROJECT_SETTINGS.fontFamily,
    transition: DEFAULT_PROJECT_SETTINGS.transition,
    fontScale: DEFAULT_PROJECT_SETTINGS.fontScale,
    chapters: [{ id: chapterId, name: '默认章节', collapsed: false }],
    slides,
  }
}

function convertLegacySlide(slide: LegacySlideContent, index: number, chapterId: string): MicroSlide {
  const elements: MicroElement[] = []

  // Title → HeadingElement
  if (slide.title) {
    elements.push({
      id: genId('el'),
      type: 'heading',
      content: slide.title,
      level: 2,
    })
  }

  // Content (markdown) → ParagraphElement
  if (slide.content) {
    elements.push({
      id: genId('el'),
      type: 'paragraph',
      content: slide.content,
    })
  }

  // Quiz → QuizElement
  if (slide.quiz) {
    elements.push(convertLegacyQuiz(slide.quiz))
  }

  // Segments from speaker_notes + audio_url
  const segments: NarrationSegment[] = []
  if (slide.speaker_notes) {
    segments.push({
      ttsText: slide.speaker_notes,
      lines: [],
      audioUrl: slide.audio_url,
    })
  }

  return {
    id: slide.slide_id || genId('slide'),
    title: slide.title || `第 ${index + 1} 页`,
    chapterId,
    order: index,
    notes: slide.speaker_notes,
    elements,
    segments,
  }
}

function convertLegacyQuiz(quiz: LegacyQuizBlock): QuizElement {
  const label = (i: number) => String.fromCharCode(65 + i)
  const content: QuizContent = {
    type:
      quiz.type === 'single_choice' ? 'choice'
      : quiz.type === 'multiple_choice' ? 'multi_choice'
      : quiz.type,
    stem: quiz.question,
    analysis: quiz.explanation,
    answer: { value: '' },
  }

  if (quiz.type === 'single_choice' || quiz.type === 'multiple_choice') {
    content.options = (quiz.options ?? []).map((opt, i) => ({ label: label(i), content: opt }))
    const count = content.options.length

    if (quiz.type === 'single_choice') {
      // answer is like "A" or "0"
      const answerStr = Array.isArray(quiz.answer) ? quiz.answer[0] : quiz.answer
      const idx = resolveOptionIndex(answerStr ?? '', count)
      content.answer.value = idx >= 0 ? label(idx) : ''
    } else {
      const answers = Array.isArray(quiz.answer) ? quiz.answer : [quiz.answer]
      content.answer.value = answers
        .map((a) => resolveOptionIndex(a, count))
        .filter((i) => i >= 0)
        .map((i) => label(i))
        .join('')
    }
  } else if (quiz.type === 'true_false') {
    const answerStr = Array.isArray(quiz.answer) ? quiz.answer[0] : quiz.answer
    content.answer.value = answerStr === 'true' || answerStr === '对' || answerStr === 'T' ? '对' : '错'
  } else if (quiz.type === 'fill_blank') {
    content.answer.value = Array.isArray(quiz.answer) ? quiz.answer.join('|') : (quiz.answer ?? '')
  }

  return {
    id: genId('el'),
    type: 'quiz',
    content,
  }
}

function resolveOptionIndex(answer: string, optionCount: number): number {
  // Try letter: A=0, B=1, ...
  const letterIdx = answer.toUpperCase().charCodeAt(0) - 65
  if (letterIdx >= 0 && letterIdx < optionCount) return letterIdx
  // Try number
  const numIdx = parseInt(answer, 10)
  if (!isNaN(numIdx) && numIdx >= 0 && numIdx < optionCount) return numIdx
  return -1
}

// ─── LessonDocument → MicrocourseProject ───────────────────────────

/**
 * 将旧版 LessonPlayerShell 的 LessonDocument 格式转换为 MicrocourseProject
 */
export function lessonDocumentToProject(doc: LegacyLessonDocument): MicrocourseProject {
  const projectId = doc.id || genId('proj')
  const chapterId = genId('ch')

  // Group blocks into a single slide (LessonDocument is flat, not paginated)
  const elements: MicroElement[] = doc.blocks.map((block) => convertLegacyBlock(block))

  const slide: MicroSlide = {
    id: genId('slide'),
    title: doc.title || '课程内容',
    chapterId,
    order: 0,
    elements,
    segments: [],
  }

  return {
    id: projectId,
    title: doc.title || '课程内容',
    theme: DEFAULT_PROJECT_SETTINGS.theme,
    customColors: { ...DEFAULT_PROJECT_SETTINGS.customColors },
    fontFamily: DEFAULT_PROJECT_SETTINGS.fontFamily,
    transition: DEFAULT_PROJECT_SETTINGS.transition,
    fontScale: DEFAULT_PROJECT_SETTINGS.fontScale,
    chapters: [{ id: chapterId, name: doc.title || '课程', collapsed: false }],
    slides: [slide],
  }
}

function convertLegacyBlock(block: LegacyLessonBlock): MicroElement {
  const id = block.id || genId('el')
  const c = block.content as Record<string, any>

  switch (block.type) {
    case 'heading':
      return { id, type: 'heading', content: c.text ?? '', level: 2 }

    case 'paragraph':
      return { id, type: 'paragraph', content: c.text ?? '' }

    case 'image':
      return { id, type: 'image', content: c.url ?? '', caption: c.caption, alt: c.caption }

    case 'video':
      return { id, type: 'video', content: c.url ?? '', caption: c.caption }

    case 'html':
      return { id, type: 'iframe', content: c.html ?? '', title: c.title, height: c.height }

    case 'quiz_single_choice':
      return {
        id, type: 'quiz',
        content: normalizeQuizContent({
          quizType: 'single_choice',
          question: c.question ?? '',
          imageUrl: c.imageUrl,
          options: c.options,
          correctOptionId: c.correctOptionId,
          explanation: c.explanation,
        }),
      }

    case 'quiz_multiple_choice':
      return {
        id, type: 'quiz',
        content: normalizeQuizContent({
          quizType: 'multiple_choice',
          question: c.question ?? '',
          imageUrl: c.imageUrl,
          options: c.options,
          correctOptionIds: c.correctOptionIds,
          explanation: c.explanation,
        }),
      }

    case 'quiz_true_false':
      return {
        id, type: 'quiz',
        content: normalizeQuizContent({
          quizType: 'true_false',
          question: c.question ?? '',
          imageUrl: c.imageUrl,
          correctAnswer: c.correctAnswer,
          explanation: c.explanation,
        }),
      }

    case 'quiz_fill_blank':
      return {
        id, type: 'quiz',
        content: normalizeQuizContent({
          quizType: 'fill_blank',
          question: c.question ?? '',
          imageUrl: c.imageUrl,
          fillText: c.text ?? '',
          explanation: c.explanation,
        }),
      }

    default:
      // Unknown block type → paragraph with JSON content
      return { id, type: 'paragraph', content: JSON.stringify(c) }
  }
}

// ─── Format Detection & Auto-Convert ───────────────────────────────

/**
 * 检测 deck JSON 的格式版本并自动转换
 */
export function normalizeDeck(deck: unknown, lessonId?: string): MicrocourseProject {
  if (!deck || typeof deck !== 'object') {
    return createEmptyProject(lessonId)
  }

  const obj = deck as Record<string, unknown>

  // Already v2 format (has slides array with elements)
  if (Array.isArray(obj.slides) && obj.slides.length > 0) {
    const firstSlide = obj.slides[0] as Record<string, unknown>
    if ('elements' in firstSlide) {
      return obj as unknown as MicrocourseProject
    }
  }

  // Legacy SlideDeck format (has slides with content string)
  if ('deck_id' in obj || (Array.isArray(obj.slides) && obj.slides.length > 0)) {
    const firstSlide = (obj.slides as any[])?.[0]
    if (firstSlide && 'content' in firstSlide && typeof firstSlide.content === 'string') {
      return slideDeckToProject(obj as unknown as LegacySlideDeck, lessonId)
    }
  }

  // Legacy LessonDocument format (has blocks array)
  if (Array.isArray(obj.blocks)) {
    return lessonDocumentToProject(obj as unknown as LegacyLessonDocument)
  }

  return createEmptyProject(lessonId)
}

// ─── Empty Project Factory ─────────────────────────────────────────

export function createEmptyProject(id?: string, title = '未命名微课'): MicrocourseProject {
  const projectId = id ?? genId('proj')
  const chapterId = genId('ch')

  return {
    id: projectId,
    title,
    theme: DEFAULT_PROJECT_SETTINGS.theme,
    customColors: { ...DEFAULT_PROJECT_SETTINGS.customColors },
    fontFamily: DEFAULT_PROJECT_SETTINGS.fontFamily,
    transition: DEFAULT_PROJECT_SETTINGS.transition,
    fontScale: DEFAULT_PROJECT_SETTINGS.fontScale,
    chapters: [{ id: chapterId, name: '第一章', collapsed: false }],
    slides: [],
  }
}

// ─── Project → Flat Slide HTML (for legacy compat) ─────────────────

/**
 * 将 MicrocourseProject 的单个 slide 渲染为简单 HTML（用于旧接口兼容）
 */
export function slideToSimpleHTML(slide: MicroSlide): string {
  const parts: string[] = []

  for (const el of slide.elements) {
    switch (el.type) {
      case 'heading':
        parts.push(`<h${el.level ?? 2}>${el.content}</h${el.level ?? 2}>`)
        break
      case 'paragraph':
        parts.push(`<div>${el.content}</div>`)
        break
      case 'math':
        parts.push(`<div class="math">${el.content}</div>`)
        break
      case 'code':
        parts.push(`<pre><code>${el.content}</code></pre>`)
        break
      case 'image':
        parts.push(`<img src="${el.content}" alt="${el.alt ?? ''}" />`)
        break
      case 'quiz':
        parts.push(`<div class="quiz">[${el.content.type}] ${el.content.stem}</div>`)
        break
      default:
        break
    }
  }

  return parts.join('\n')
}

export { genId, FORMAT_VERSION_CURRENT }
